import { z } from 'zod';

import { CompanyId, CustodialAccountBalanceId, CustodialAccountId, UserId } from '../BrandedIds';
import { FIVE_TO_SEVENTEEN_DIGITS, NINE_DIGITS } from '../utils/bankNumberPatterns';
import { zodDateOrString } from '../utils/Zod';

// Buffer is a Node global, undefined in browsers. The runtime check stays safe in
// both environments and is a no-op in the browser, where the encrypted accountNumber
// blob never flows to the client. Mirrors the pattern used by Pool.
const isBufferOrAbsent = (v: unknown): boolean => v == null || (typeof Buffer !== 'undefined' && Buffer.isBuffer(v));

export const CustodialAccount = z.object({
  id: CustodialAccountId,
  companyId: CompanyId,
  name: z.string().optional(),
  bankName: z.string().optional(),
  // Encrypted blob; only ever populated server-side.
  accountNumber: z.custom<Buffer>(isBufferOrAbsent).optional(),
  routingNumber: z.string().optional(),
  lastFour: z.string().optional(),
  createdAt: zodDateOrString,
  updatedAt: zodDateOrString,
});
export type CustodialAccount = z.infer<typeof CustodialAccount>;

export const CustodialAccountBalance = z.object({
  id: CustodialAccountBalanceId,
  custodialAccountId: CustodialAccountId,
  balance: z.number(),
  asOfDate: zodDateOrString,
  enteredAt: zodDateOrString,
  enteredById: UserId,
  createdAt: zodDateOrString,
  updatedAt: zodDateOrString,
});
export type CustodialAccountBalance = z.infer<typeof CustodialAccountBalance>;

export const CreateCustodialAccountInput = z.object({
  name: z.string().min(1).max(120),
  bankName: z.string().max(120).optional(),
  accountNumber: z.string().regex(FIVE_TO_SEVENTEEN_DIGITS, 'Must be 5-17 digits'),
  routingNumber: z.string().regex(NINE_DIGITS, 'Must be 9 digits'),
});
export type CreateCustodialAccountInput = z.infer<typeof CreateCustodialAccountInput>;

// All fields optional: an update only touches what's provided. accountNumber, when
// present, must still be well-formed; when absent it's left unchanged.
export const UpdateCustodialAccountInput = z.object({
  name: z.string().min(1).max(120).optional(),
  bankName: z.string().max(120).optional(),
  accountNumber: z.string().regex(FIVE_TO_SEVENTEEN_DIGITS, 'Must be 5-17 digits').optional(),
  routingNumber: z.string().regex(NINE_DIGITS, 'Must be 9 digits').optional(),
});
export type UpdateCustodialAccountInput = z.infer<typeof UpdateCustodialAccountInput>;

export const RecordCustodialAccountBalanceInput = z.object({
  balance: z.number(),
  asOfDate: zodDateOrString,
});
export type RecordCustodialAccountBalanceInput = z.infer<typeof RecordCustodialAccountBalanceInput>;

// The role a Pool plays a custodial account in. Each value maps to a Pool.<role>CustodialAccountId
// FK column; the same account may serve multiple roles. P&I and T&I are required for GNMA pools.
export const CustodialAccountRole = z.enum([
  'principalAndInterest',
  'taxAndInsurance',
  'clearing',
  'servicingFees',
  'suspense',
  'guaranteeFees',
]);
export type CustodialAccountRole = z.infer<typeof CustodialAccountRole>;
